import React, {useEffect, useState} from 'react';
import * as yup from 'yup';
import {Formik, Form, FieldArray} from 'formik';
import Select from 'react-select';
import {useTranslation} from 'react-i18next';
import { constants } from "../constants/constants";
import deleteCheckRowIcon from "../../images/new-dlt.png";
import PlusIcon from "../../images/plus.png";
import {selectCustomStyles} from '../shared/select.styled';
import {StyledChequeForm} from './paymentFlow.styled';
import {Datepicker} from '../Datepicker';
import GeneralPopUp from '../simple/popUpFunctional';
import BllinkUtils from '../utils/utils';
import {GAEvent} from '../google_analytics/google_analytics';
import LastConfirmationPageLogic from '../../tenants/tenant_payments/lastConfirmationPageLogic';
import {useDispatch, useSelector} from 'react-redux';
import ServerData from '../serverData';
import {hideLoader, showLoader} from '../../store/common/slice';
import {useHistory} from 'react-router-dom';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {triggerRewardsNotification} from '../../managers/rewards/rewardsEvent';
import {Currency} from "../currency";
import {fetchBuildingData} from "../../store/buildings/actions";

const BllinkMoney = BllinkUtils.Money;
const JsExtras = BllinkUtils.JsExtras;
const StringUtils = BllinkUtils.Strings;

/// moved to be rendered as a separate page and not as a pop up because it caused so many bugs

export default function ChequeForm({amount, buildingID, apartmentNumber}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const [rowsToAdd, setRowsToAdd] = useState(1);
    const [showAlertPopup, setShowAlertPopup] = useState(false);
    const [popupText, setPopupText] = useState({
        title: t('payments.offline_payment_amounts_are_wrong'),
        text: ''
    });
    const [showEmptyModal, setShowEmptyModal] = useState(false);
    const [bankOptions, setBankOptions] = useState([]);
    const accessToken = useSelector((state) => state.auth.accessToken);
    let client = new ServerData(null, null, {accessToken: accessToken});

    useEffect(async () => {
        if (buildingID != null) {
            const {payload: {currency}} = await dispatch(fetchBuildingData({buildingID}));
            const codes = currency.toUpperCase() === 'MXN' ? constants.bankCodesInMexico : constants.bankCodesInIsrael;
            const country = currency.toUpperCase() === 'MXN' ? 'mexico' : 'israel';
            setBankOptions(codes.map(value => ({ value, label: `${value} - ${t(`common.banks.${country}.${value}`)}` })));
        }
    }, [buildingID]);

    const initialFormValues = {
        cheques: [{
            chequeNumber: '',
            date: '',
            amount: ''
        }]
    };
    const validationSchema = yup.object().shape({
        branch: yup.string().matches(/^\d+$/, t('titles.forms.number_field')),
        accountNumber: yup.string(),
        bankCode: yup.object(),
        cheques: yup.array()
            .of(
                yup.object().shape({
                    chequeNumber: yup.number().required(t('titles.forms.mandatory_field')),
                    date: yup.string().required(t('titles.forms.mandatory_field')),
                    amount: yup.string().matches(/^-?\d+\.?\d*$/, t('titles.forms.number_field')).required(t('titles.forms.mandatory_field')),
                })
            )
    });

    const setRowsAmount = event => {
        // set default value
        if (!event.target.value) {
            setRowsToAdd(1);
            return;
        }

        setRowsToAdd(event.target.value.replace(/\D/,''));
    }

    const addFields = (formikPush, values) => {
        // try to populate fields by last row https://app.asana.com/0/1196545425570329/1199619028019062
        const lastRow = values.cheques[values.cheques.length - 1];

        [...Array(parseInt(rowsToAdd)).keys()].forEach((val, index) => {
            const date = new Date(lastRow.date);
            date.setMonth(date.getMonth() + index + 1);

            const row = {
                amount: lastRow.amount,
                chequeNumber: lastRow.chequeNumber ? +lastRow.chequeNumber + index + 1 : '',
                date: lastRow.date ? new Date(date) : ''
            };
            formikPush(row);
        });
    };

    const validateTotalAmount = (values) => {
        const totalPaymentsSent = BllinkMoney.roundNumber(JsExtras.sum(values, 'amount') || 0, 2);
        const totalPaymentsShouldHaveBeenSent = BllinkMoney.roundNumber(amount.value, 2);

        if (totalPaymentsSent !== totalPaymentsShouldHaveBeenSent) {
            setPopupText({
                ...popupText,
                text: t('payments.offline_payment_amounts_are_wrong_text',
                    {
                        amountsSent: StringUtils.numberWithCommas(totalPaymentsSent),
                        totalToPay: StringUtils.numberWithCommas(totalPaymentsShouldHaveBeenSent)
                    })
            });

            GAEvent(constants.GoogleAnalytics.Events.popUps, 'popUpRender', 1, 'amountsSentOfflinePaymentsAreWrong');
            setShowAlertPopup(true);
            return false;
        }

        return true;
    };

    const buildPayload = (values) => {
        const payingFor = new LastConfirmationPageLogic({
            buildingID,
            apartmentNumber,
            paymentMethod: constants.managerPaymentsOptions.chequeServerKey
        }).buildingPaymentPayload({ignoreMissingInstallments: true});

        const cheques = values.cheques.map(cheque => ({
            amount: {
                value: parseFloat(cheque.amount),
                currency: amount.currency.toLowerCase()
            },
            paymentMethod: constants.managerPaymentsOptions.chequeServerKey,
            dueDate: cheque.date,
            extraDetails: {
                chequeID: cheque.chequeNumber,
                accountNumber: values.accountNumber,
                bankCode: values.bankCode?.value,
                branchNumber:values.branch
            }
        }))

        return {
            paying_for: payingFor,
            tenant_details: payingFor.tenant_details,
            buildingID,
            apartmentNumber,
            totalToPay: amount.value,
            cheques
        }
    };

    const createPaymentPlan = (values) => {
        const payload = buildPayload(values);
        const { paying_for: { tenant_details, on_going_payments, one_time_payments, past_payments } } = payload;
        if (!tenant_details || (!on_going_payments && !one_time_payments && !past_payments)) {
            setShowEmptyModal(true);
            return
        } else {
            if (buildingID == null || apartmentNumber == null) {
                console.error('[chequeForm] incorrect API call, path parameters are undefined');
            }
            return client.post(`/managers/external-payments/${buildingID}/${apartmentNumber}`, payload);
        }
    }

    const submitForm = async (evt, values) => {
        evt.preventDefault();
        dispatch(showLoader());

        const isAmountValid = validateTotalAmount(values.cheques);

        if (!isAmountValid) {
            dispatch(hideLoader());
            return;
        }

        const result = await createPaymentPlan(values);

        GAEvent(constants.GoogleAnalytics.Events.offlinePayments,
            'successAddPayment',
            amount.value,
            constants.managerPaymentsOptions.chequeServerKey);

        dispatch(hideLoader());

        const cartID = result?.cartID;
        if (cartID) {
            triggerRewardsNotification();
            history.push({pathname: `/${constants.pages.managerConfirmation}/${cartID}`});
        }
    }

    const renderChequeRow = (index, {handleChange, handleBlur, remove, setFieldValue, setFieldTouched, values, errors, touched}) => {
        const deleteIcon = index > 0 ? <img alt="delete-row" src={deleteCheckRowIcon}/> : '';

        return (
            <div className='row-cheque-container'>
                <div className="form-group input-container">
                    <label htmlFor="Check-number">{`${t('offline_payments.cheque_number')} ${index + 1} :*`}</label>
                    <input
                        type="text"
                        className={`form-control ${errors && touched && errors[index]?.chequeNumber && touched[index]?.chequeNumber ? 'error' : ''}`}
                        name={`cheques[${index}].chequeNumber`}
                        value={values[index].chequeNumber || ''}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                    {
                        errors && touched && errors[index]?.chequeNumber && touched[index]?.chequeNumber && (
                            <label className={'error'}>{errors[index]?.chequeNumber}</label>
                        )
                    }
                </div>
              
                <div className="form-group">
                    <label htmlFor="email">{t('offline_payments.date')}:*</label>
                    <div className={`${errors && touched && errors[index]?.date && touched[index]?.date ? 'error' : ''}`}>
                        <Datepicker
                            inputProps={{
                                name: `cheques[${index}].date`
                            }}
                            defaultValue={values[index].date || undefined}
                            placeholder={'dd/mm/yyyy'}
                            onDayChange={selectedDay => setFieldValue(`cheques[${index}].date`, selectedDay && selectedDay.toISOString())}
                            onDayPickerHide={() => setFieldTouched(`cheques[${index}].date`, true)}
                        />
                    </div>
                    {
                        errors && touched && errors[index]?.date && touched[index]?.date && (
                            <label className={'error'}>{errors[index]?.date}</label>
                        )
                    }
                </div>
                  
                <div className="form-group input-container">
                    <label htmlFor="email">{t('offline_payments.amount')}:*</label>
                    <input
                        type="text"
                        className={`form-control ${errors && touched && errors[index]?.amount && touched[index]?.amount ? 'error' : ''}`}
                        name={`cheques[${index}].amount`}
                        value={values[index].amount || ''}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                    {
                        errors && touched && errors[index]?.amount && touched[index]?.amount && (
                            <label className={'error'}>{errors[index]?.amount}</label>
                        )
                    }
                </div>
                        
                <div
                    className="remove-cheque-n"
                    onClick={() => remove(index)}
                >
                    {deleteIcon}
                </div>
            </div>
        )
    }

    return (
        <StyledChequeForm className="col-md-12">
            <div className="modal-content n_content">
                <div className="modal-header">
                    <h5 className="col-12 modal-title text-center">{t('offline_payments.pay_with_cheque_title')} </h5>
                </div>
                <div className="modal-body">
                    <Formik
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                    >
                        {({
                              errors,
                              touched,
                              values,
                              handleChange,
                              handleBlur,
                              isValid,
                              dirty,
                              setFieldValue,
                              setFieldTouched
                        }) => (
                            <Form className="offline-payment-body js-offline-payment-form js-cheque-form">
                                <FieldArray name="cheques">
                                    {({remove, push}) => (
                                        <>                                             
                                            <div className='row-cheque-container margin-bottom'> 
                                                <div className="form-group js-bank-code">
                                                    <label
                                                        htmlFor="bank">{t('offline_payments.bank_code_title')}: </label>
                                                    <Select
                                                        styles={selectCustomStyles}
                                                        options={bankOptions}
                                                        onChange={type => setFieldValue('bankCode', type)}
                                                        onBlur={() => setFieldTouched('bankCode', true)}
                                                        value={values.bankCode || ''}
                                                    />
                                                </div>
                                            
                                                <div className="form-group input-container">
                                                    <label
                                                        htmlFor="branch">{t('offline_payments.bank_branch_title')}: </label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.branch && touched.branch ? 'error' : ''}`}
                                                        name="branch"
                                                        value={values.branch || ''}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                    {
                                                        errors.branch && touched.branch && (
                                                            <label className={'error'}>{errors.branch}</label>
                                                        )
                                                    }
                                                </div>
                                            
                                                <div className="form-group input-container">
                                                    <label
                                                        htmlFor="account-number">{t('offline_payments.bank_account_number_title')}:</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="accountNumber"
                                                        value={values.accountNumber || ''}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                </div>
                                            </div>                                                                                                                                        

                                            {values.cheques.length > 0 &&
                                                values.cheques.map((friend, index) => renderChequeRow(index, {
                                                    handleChange,
                                                    handleBlur,
                                                    remove,
                                                    setFieldValue,
                                                    setFieldTouched,
                                                    values: values.cheques,
                                                    errors: errors.cheques,
                                                    touched: touched.cheques
                                                }))
                                            }
                                            <div className="row">
                                                <div className="col-sm-6">
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={props => (
                                                            <Tooltip {...props}>
                                                                {t('offline_payments.add_row_tooltip')}
                                                            </Tooltip>
                                                        )}
                                                    >
                                                        <div className="add-rows-field">
                                                            <input
                                                                className="form-control bllink-input-border"
                                                                type="text"
                                                                placeholder={t('offline_payments.cheque_number_placeholder')}
                                                                value={rowsToAdd}
                                                                onChange={setRowsAmount}
                                                            />
                                                            <div id="addBtn-new">
                                                                <img
                                                                    className="js-add-new-btn"
                                                                    src={PlusIcon}
                                                                    alt={"new line"}
                                                                    onClick={() => addFields(push, values)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className="col-sm-2">
                                                    <div className="form-group">
                                                        <label>{t('offline_payments.amount')}: </label>
                                                        <br/>
                                                        <label>{amount.value} <Currency>{amount.currency}</Currency></label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <button
                                                        type="submit"
                                                        className="bllink-button"
                                                        disabled={!(isValid && dirty)}
                                                        onClick={(evt) => submitForm(evt, values)}
                                                    >
                                                        {t('offline_payments.make_offline_payment_button')}
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </FieldArray>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>

            <GeneralPopUp
                show={showAlertPopup}
                onHideModal={() => setShowAlertPopup(false)}
                text={popupText.text}
                title={popupText.title}
            />

            <GeneralPopUp
                show={showEmptyModal}
                onHideModal={() => setShowEmptyModal(false)}
                text={t('payments.cart_empty_text')}
                title={t('payments.cart_empty_title')}
            />

        </StyledChequeForm>
    )
}
